
import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Fab,
  Zoom,
  Grid,
  Paper,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SearchIcon from '@mui/icons-material/Search';
import StarsIcon from '@mui/icons-material/Stars';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { Link } from 'react-router-dom';

const features = [
  {
    title: 'Secure Upload',
    description: 'Register gadgets to prevent resale after theft.',
    icon: <CloudUploadIcon sx={{ fontSize: 36, color: '#fff' }} />,
    bg: '#1976d2',
    link: '/upload',
  },
  {
    title: 'Verify Devices',
    description: 'Check gadget legitimacy using serial number or IMEI.',
    icon: <SearchIcon sx={{ fontSize: 36, color: '#fff' }} />,
    bg: '#2e7d32',
    link: '/search',
  },
  {
    title: 'Premium Access',
    description: 'Unlock reports, theft alerts, and device history.',
    icon: <StarsIcon sx={{ fontSize: 36, color: '#fff' }} />,
    bg: '#ed6c02',
    link: '/subscribe',
  },
];

const Home = () => {
  const [showScroll, setShowScroll] = useState(false);

  useEffect(() => {
    const onScroll = () => setShowScroll(window.scrollY > 150);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  return (
    <Box>
      {/* HERO SECTION */}
      <Box
        sx={{
          py: 2,
          background: 'linear-gradient(135deg, #004aad, #1565c0)',
          color: '#fff',
          textAlign: 'center',
          minHeight: '280px',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Container>
          <Typography variant="h4" fontWeight="900" sx={{ mb: 1 }}>
            Welcome to ProTechTion
          </Typography>
          <Typography
            variant="body2"
            sx={{ mb: 1, fontSize: '0.95rem', color: 'rgba(255,255,255,0.85)' }}
          >
            Secure your gadgets. Verify ownership. Prevent Theft.
          </Typography>
          <Button
            component={Link}
            to="/register"
            variant="contained"
            color="warning"
            size="small"
          >
            Create Account
          </Button>
        </Container>
      </Box>

      {/* FEATURES SECTION */}
      <Container sx={{ py: 5 }}>
        <Typography variant="h5" align="center" fontWeight="bold" sx={{ mb: 4 }}>
          Why Choose Us
        </Typography>
        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card
                elevation={4}
                sx={{
                  height: '100%',
                  p: 3,
                  borderRadius: 3,
                  textAlign: 'center',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: 8,
                    transform: 'translateY(-6px)',
                  },
                }}
              >
                <Box
                  sx={{
                    backgroundColor: feature.bg,
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                    mb: 2,
                  }}
                >
                  {feature.icon}
                </Box>
                <CardContent sx={{ pb: 1 }}>
                  <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
                    {feature.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: '0.9rem', mb: 2 }}
                  >
                    {feature.description}
                  </Typography>
                  <Button
                    component={Link}
                    to={feature.link}
                    variant="outlined"
                    size="small"
                  >
                    Learn More
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* CTA SECTION */}
      <Box
        sx={{
          py: 5,
          backgroundColor: '#f1f5f9',
        }}
      >
        <Container maxWidth="sm">
          <Paper
            elevation={6}
            sx={{
              p: 4,
              borderRadius: 4,
              textAlign: 'center',
              background: 'linear-gradient(to right, #1976d2, #004aad)',
              color: '#fff',
            }}
          >
            <Typography variant="h5" fontWeight="bold" sx={{ mb: 2 }}>
              Get Started with ProTechTion
            </Typography>
            <Typography variant="body2" sx={{ mb: 3, opacity: 0.9 }}>
              Already have an account? Log in or register to manage and verify your devices today.
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Button
                component={Link}
                to="/login"
                variant="contained"
                color="success"
                size="small"
              >
                Log In
              </Button>
              <Button
                component={Link}
                to="/register"
                variant="outlined"
                color="inherit"
                size="small"
                sx={{ borderColor: '#fff', color: '#fff', '&:hover': { borderColor: '#eee' } }}
              >
                Create Account
              </Button>
            </Box>
          </Paper>
        </Container>
      </Box>

      {/* SCROLL TO TOP */}
      <Zoom in={showScroll}>
        <Fab
          color="primary"
          size="small"
          onClick={scrollToTop}
          sx={{
            position: 'fixed',
            bottom: 60,
            right: 15,
            zIndex: 1000,
          }}
        >
          <ArrowUpwardIcon />
        </Fab>
      </Zoom>
    </Box>
  );
};

export default Home;
